import { ArrowRight } from "lucide-react";
import BodyScanAnimation from "./BodyScanAnimation";
import FaceScanAnimation from "./FaceScanAnimation";
import TypewriterScene from "./TypewritterScene";
import { useContext, useMemo, useState } from "react";
import MuseScreenRenderer from "./MuseScreenRenderer";
import Header from "../../Header";
import { GenderType, MuseScreenStep } from "../../../utils/enums";
import {
	bodyScanPerson,
	faceScanPerson,
	faceScanVideo,
	femaleScanVideo,
	fittingGuide,
	formFittingGuide,
	fullText,
	leSableDress,
	maleFaceScanVideo,
	maleFormFittingGuide,
	maleScanVideo,
	maleSizeSuggestions,
	OUTFIT_IMAGES,
	sizeSuggestions,
} from "../../..//utils/constants";
import { MuseStepsProps } from "../../../types/interfaces";
import useMuseAnimations from "../../../customHooks/useMuseAnimation";
import SpecificButton from "../../../atoms/specificButton/SpecificButton";
import { LanguageKeys } from "../../../utils/languageKeys";
import { LanguageContext } from "../../../utils/context/languageContext";
import { useConfig } from "../../../utils/context/configContext";

const screens: {
	id: string;
	title: string;
	description: string;
	image: string;
	component?: React.ReactNode;
}[] = [
	{
		id: MuseScreenStep.Body,
		title: LanguageKeys.bodyScan,
		description: LanguageKeys.bodyScanDescription,
		image: bodyScanPerson,
		component: <BodyScanAnimation />,
	},
	{
		id: MuseScreenStep.Face,
		title: LanguageKeys.faceScan,
		description: LanguageKeys.faceScanDescription,
		image: faceScanPerson,
		component: <FaceScanAnimation />,
	},
	{
		id: MuseScreenStep.SizeFit,
		title: LanguageKeys.sizeFit,
		description: "",
		image: leSableDress,
		component: <TypewriterScene />,
	},
	{
		id: MuseScreenStep.Ready,
		title: LanguageKeys.readyToStart,
		description: LanguageKeys.readyToStartDescription,
		image: fittingGuide,
	},
];

export default function MuseSteps({ applicableScreens, gender, onNext, isCustom }: Omit<MuseStepsProps, "config">) {
	const { translate } = useContext(LanguageContext) || {};
	const config = useConfig();
	const [currentScreen, setCurrentScreen] = useState(0);
	const [scanLinePosition, setScanLinePosition] = useState(0);
	const [faceZoomed, setFaceZoomed] = useState(false);
	const [faceScanLinePosition, setFaceScanLinePosition] = useState(0);
	const [showFaceScanLine, setShowFaceScanLine] = useState(false);
	const [currentOutfit, setCurrentOutfit] = useState(0);
	const [typewriterText, setTypewriterText] = useState("");
	const [showLargeS, setShowLargeS] = useState(false);

	const screensToShow = useMemo(() => {
		if (isCustom) {
			return screens.reduce((acc: any, el) => {
				if (["face", "sizeFit"].includes(el.id)) {
					return acc;
				}
				acc.push(el);
				return acc;
			}, []);
		}
		if (!applicableScreens?.length) {
			return screens;
		}
		return screens.filter((screen) => applicableScreens.includes(screen.id));
	}, [applicableScreens]);

	useMuseAnimations({
		screenId: screensToShow[currentScreen].id,
		setScanLinePosition,
		setFaceZoomed,
		setShowFaceScanLine,
		setFaceScanLinePosition,
		setCurrentOutfit,
		setTypewriterText,
		setShowLargeS,
		outfitImages: OUTFIT_IMAGES,
		fullText,
	});

	const onNextClick = () => {
		if (currentScreen < screensToShow.length - 1) {
			setCurrentScreen((prev) => prev + 1);
		} else {
			onNext?.();
		}
	};
	return (
		<div data-testid="educational-root" data-educational-step="1" className="relative common-ui-main h-full max-w-[28rem] mx-auto w-full flex justify-center" style={{ background: config?.style?.base?.backgroundColor }}>
			<div className="absolute bottom-0 left-0 right-0 h-full  shadow-lg w-full">
				<div className="h-full flex flex-col p-[1rem] w-full">
					<Header noTitle />
					<div className="text-center pb-[.5rem]">
						<h1
							style={{
								fontFamily: config?.style?.heading?.headingFontFamily || "SeriouslyNostalgic Fn",
								fontSize: config?.style?.heading?.headingFontSize || "32px",
								color: config?.style?.heading?.headingColor || "#000",
								fontWeight: config?.style?.heading?.headingFontWeight || "normal",
							}}
						>
							{translate?.(screensToShow[currentScreen].title)}
						</h1>
						{screensToShow[currentScreen].description && (
							<p
								className=" mt-[.25rem] max-w-[280px] mx-auto min-h-[40px]"
								style={{
									fontFamily: config?.style?.subheading?.subheadingFontFamily || "'Inter', sans-serif",
									fontSize: config?.style?.subheading?.subheadingFontSize || "14px",
									color: config?.style?.subheading?.subheadingColor || "#4b5563",
									fontWeight: config?.style?.subheading?.subheadingFontWeight || "normal",
								}}
							>
								{translate?.(screensToShow[currentScreen].description)}
							</p>
						)}
					</div>
					<div className="flex-1 flex justify-center items-center overflow-hidden py-[1rem] relative">
						<MuseScreenRenderer
							screenIndex={currentScreen}
							scanLinePosition={scanLinePosition}
							faceZoomed={faceZoomed}
							showFaceScanLine={showFaceScanLine}
							faceScanLinePosition={faceScanLinePosition}
							outfitImages={OUTFIT_IMAGES}
							currentOutfit={currentOutfit}
							showLargeS={showLargeS}
							typewriterText={typewriterText}
							fullText={fullText}
							screens={screensToShow}
							videoToShow={gender === GenderType.Male ? maleScanVideo : femaleScanVideo}
							faceScanVideo={gender === GenderType.Male ? maleFaceScanVideo : faceScanVideo}
							sizeVideo={gender === GenderType.Male ? maleSizeSuggestions : sizeSuggestions}
							formFittingVideo={gender === GenderType.Male ? maleFormFittingGuide : formFittingGuide}
						/>
					</div>

					<div className="pt-[.5rem] flex justify-end">
						<SpecificButton buttonText={translate?.(LanguageKeys.next)} type="submit" data-testid="educational-next-btn" postfixIcon={<ArrowRight size={14} />} buttonFunc={onNextClick} />
					</div>
				</div>
			</div>
		</div>
	);
}
